import { injectable } from 'inversify';

import Toast from '../components/ui/toast/toast.component';

interface ToastOptions {
    /**
     * INERT, and left that way deliberately (lane N, 2026-09-05).
     *
     * `disableTimeOut`, `closeButton` and `tapToDismiss` are ngx-toastr's names; nothing here has
     * ever forwarded them and `Toast` has no props by those names, so the one live caller that
     * passes them (`photo-editor.component.tsx:1091`, `disableTimeOut: true` + `tapToDismiss:
     * true`) has always got the default 3 s auto-dismiss. `Toast.mustClose` is the prop that would
     * honour the first of them. Wiring it up would change a production toast in another feature
     * on the strength of an inference about what its author meant, so it is recorded here rather
     * than done: the three rows below stay accepted-and-ignored, exactly as before.
     */
    disableTimeOut?: boolean;
    timeout?: number;
    closeButton?: boolean;
    tapToDismiss?: boolean;
    /** Forwarded to `Toast` — see its props for what each one is for (lane N, 2026-09-05). */
    canClose?: boolean;
    mustClose?: boolean;
    role?: 'status' | 'alert';
    copyKey?: string;
    closeLabel?: string;
    /** A mount point other than the page's `#toasts` -- see `ToastProps.host` (Studio, round 4 G5). */
    host?: HTMLElement | null;
    /** The action slot (F15) -- see `Toast`'s props. Both or neither; one alone renders nothing. */
    actionLabel?: string;
    onAction?: (ref: ToastHandle) => void;
    onOpen?: (ref: ToastHandle) => void;
    onClose?: (ref: ToastHandle) => void;
}

/**
 * What a caller may do with a toast it raised: take it off screen again.
 *
 * `Toast` itself is the object handed to `onOpen`/`onClose`; this is the part of it a caller is
 * allowed to know about, so a feature can dedupe or cap its own stack (see
 * `components/pdf-editor/views/messages.ts`) without reaching into a component's internals.
 */
export interface ToastHandle {
    unmount(): void;
}

/**
 * The close control's accessible name for `undoable`, which is the one method here that raises a
 * TITLELESS toast -- so without it the × is the only unnamed control in the message (WCAG 4.1.2).
 *
 * It lives here rather than in the Studio's copy table because the table
 * (`components/studio/views/studio.copy.ts`) imports `components/studio/model/limits`, and the
 * Phase 5a gate's ninth prohibition is that no service may import `model/` -- that is the
 * 11-service inversify SCC that TDZ-crashes the SSG build. A caller that wants its own word
 * passes `options.closeLabel`, which still wins.
 */
export const UNDOABLE_CLOSE_LABEL = 'Dismiss';

@injectable()
export class ToastService {
    public success(title: string, message: string, options: ToastOptions = {}) {
        Toast.showToast({
            ...options,
            title: title,
            message: message,
            type: 'success'
        });
    }

    public warning(title: string, message: string, options: ToastOptions = {}) {
        Toast.showToast({
            ...options,
            title: title,
            message: message,
            type: 'warning'
        });
    }

    public error(title: string, message: string, options: ToastOptions = {}) {
        Toast.showToast({
            ...options,
            title: title,
            message: message,
            type: 'danger'
        });
    }

    /**
     * The neutral tone (lane N, 2026-09-05).
     *
     * `Toast` has carried an `info` type since it was written and no method reached it, so a
     * message that reports something rather than celebrating or refusing had to borrow
     * `success`'s green tick. "Switched to Liberation Sans to add characters the original font
     * doesn't have" is not a success and not a warning; it is a note. Additive — no existing
     * caller changes tone.
     */
    public info(title: string, message: string, options: ToastOptions = {}) {
        Toast.showToast({
            ...options,
            title: title,
            message: message,
            type: 'info'
        });
    }

    /**
     * A neutral message with one way back -- "Removed beach-03.jpg · Undo" (Studio 6.8).
     *
     * A method rather than four call sites passing the same five options, because the shape is
     * fixed by the copy rule: no title (the sentence IS the message, as for the PDF editor's
     * notices), `role="status"` so it is announced, and 8 s rather than 3 -- long enough to read
     * a filename and reach for the button, short enough not to sit over the timeline.
     */
    public undoable(
        message: string,
        actionLabel: string,
        onAction: (ref: ToastHandle) => void,
        options: ToastOptions = {},
    ) {
        Toast.showToast({
            timeout: 8000,
            role: 'status',
            closeLabel: UNDOABLE_CLOSE_LABEL,
            ...options,
            message,
            actionLabel,
            onAction,
            type: 'info',
        });
    }

}